import { ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { ErrorCard, Stat, Td, Th, Verdict, formatDate, formatNumber, formatPercent, signColor } from '../components/research';
import { useRunDetail } from '../hooks/usePrograms';

function money(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return `${value >= 0 ? '+' : '-'}${Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function RunDetail() {
    const { runId } = useParams<{ runId: string }>();
    const { run, isLoading, error, refresh } = useRunDetail(runId);

    const back = (
        <Link to="/programs" className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--foreground-muted)' }}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Programs
        </Link>
    );

    if (error) {
        return (
            <div className="flex flex-col gap-4">
                {back}
                <ErrorCard message={error} onRetry={refresh} />
            </div>
        );
    }

    if (!run) {
        return (
            <div className="py-16 text-center" style={{ color: 'var(--foreground-muted)' }}>
                {isLoading ? 'Loading…' : 'Run not found.'}
            </div>
        );
    }

    const overrides = Object.entries(run.overrides ?? {});

    return (
        <div className="flex min-w-0 flex-col gap-4">
            <section className="px-1">
                {run.program_key ? (
                    <Link to={`/programs/${run.program_key}`} className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--foreground-muted)' }}>
                        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {run.program_key}
                    </Link>
                ) : (
                    back
                )}
                <h2 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
                    {run.subject_key} <span style={{ color: 'var(--foreground-muted)' }}>· {run.sample}</span>
                </h2>
                <p className="mt-1 text-sm leading-6" style={{ color: 'var(--foreground-muted)' }}>
                    {run.subject_kind === 'strategy' ? 'Bare-strategy run (predates the program-only rule) · ' : ''}
                    {run.num_windows} × {run.window_days}-day windows · {run.constituent_count} instruments ·{' '}
                    {formatDate(run.period_start)} → {formatDate(run.period_end)} · balance{' '}
                    {run.initial_balance?.toLocaleString() ?? '—'}
                    {run.leverage ? ` · ${run.leverage}× venue leverage` : ''}
                </p>
                {run.verdict_reason ? (
                    <p className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                        <Verdict passed={run.passed} />
                        <span style={{ color: 'var(--foreground-muted)' }}>{run.verdict_reason}</span>
                    </p>
                ) : null}
                {overrides.length ? (
                    <p className="mt-2 text-xs" style={{ color: 'var(--foreground-muted)' }}>
                        overrides: {overrides.map(([k, v]) => `${k}=${String(v)}`).join(', ')}
                    </p>
                ) : null}
                {/* Which code produced this. Two runs of one program that do not share a
                    subject hash are not two measurements of the same thing. */}
                <p className="mt-2 text-xs" style={{ color: 'var(--foreground-muted)' }}>
                    {run.code_subject_hash ? (
                        <>
                            code {run.code_subject_hash.slice(0, 12)}
                            {run.code_git_commit ? ` · git ${run.code_git_commit.slice(0, 8)}` : ''}
                            {run.code_git_dirty ? ' (uncommitted)' : ''} · restore with{' '}
                            <code>scripts/show_code_version.py --run {run.id}</code>
                        </>
                    ) : (
                        'code snapshot not captured — this run predates code versioning'
                    )}
                </p>
            </section>

            <div className="surface-card rounded-[28px] p-6 sm:p-7">
                <div className="grid grid-cols-2 gap-5 sm:grid-cols-4 lg:grid-cols-7">
                    <Stat label="Net" value={formatPercent(run.cumulative_return)} color={signColor(run.cumulative_return)} />
                    <Stat label="Gross" value={formatPercent(run.gross_return)} />
                    <Stat label="Costs" value={run.cost_drag === null ? '—' : `-${Math.abs(run.cost_drag).toFixed(2)}%`} />
                    <Stat label="Funding" value={formatPercent(run.funding_return)} color={signColor(run.funding_return)} />
                    <Stat label="Sharpe" value={formatNumber(run.sharpe)} color={signColor(run.sharpe)} hint="annualised" />
                    <Stat label="Worst" value={formatPercent(run.worst_window_return)} color={signColor(run.worst_window_return)} />
                    <Stat label="Trades" value={run.num_trades ?? '—'} />
                </div>
            </div>

            <div className="surface-card rounded-[28px] p-6 sm:p-7">
                <h3 className="text-lg font-semibold">Windows</h3>
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full min-w-[920px] border-collapse text-sm">
                        <thead>
                            <tr style={{ color: 'var(--foreground-muted)' }}>
                                <Th>Window end</Th>
                                <Th align="right">Net %</Th>
                                <Th align="right">P&amp;L</Th>
                                <Th align="right">Gross</Th>
                                <Th align="right">Funding</Th>
                                <Th align="right">Costs</Th>
                                <Th align="right">Trades</Th>
                                <Th align="right">L/S</Th>
                                <Th align="right">Win %</Th>
                                <Th align="right">PF</Th>
                                <Th align="right">Max DD</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {run.windows.map((window) => (
                                <tr key={window.id} className="task-row">
                                    <Td mono>{window.window_end}</Td>
                                    <Td align="right" mono color={signColor(window.return_pct)}>
                                        {window.ok ? formatPercent(window.return_pct) : 'ERR'}
                                    </Td>
                                    <Td align="right" mono color={signColor(window.net_pnl)}>
                                        {money(window.net_pnl)}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {money(window.gross_pnl)}
                                    </Td>
                                    <Td align="right" mono color={signColor(window.funding_pnl)}>
                                        {money(window.funding_pnl)}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {window.transaction_costs === null ? '—' : `-${Math.round(Math.abs(window.transaction_costs)).toLocaleString()}`}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {window.num_trades ?? '—'}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {window.num_long ?? '—'}/{window.num_short ?? '—'}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {window.win_rate_pct === null ? '—' : window.win_rate_pct.toFixed(0)}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {window.profit_factor === null ? '—' : window.profit_factor.toFixed(2)}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {window.max_drawdown_pct === null ? '—' : `${window.max_drawdown_pct.toFixed(2)}%`}
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="surface-card rounded-[28px] p-6 sm:p-7">
                <h3 className="text-lg font-semibold">Per-instrument contribution</h3>
                <p className="mt-1 text-xs" style={{ color: 'var(--foreground-muted)' }}>
                    Summed across every window — a book that is really one lucky leg shows here.
                </p>
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full min-w-[420px] border-collapse text-sm">
                        <thead>
                            <tr style={{ color: 'var(--foreground-muted)' }}>
                                <Th>Instrument</Th>
                                <Th align="right">P&amp;L</Th>
                                <Th align="right">Windows +</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {run.instruments.map((instrument) => (
                                <tr key={instrument.instrument} className="task-row">
                                    <Td mono>{instrument.instrument}</Td>
                                    <Td align="right" mono color={signColor(instrument.pnl)}>
                                        {money(instrument.pnl)}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {instrument.windows_positive}/{instrument.windows}
                                    </Td>
                                </tr>
                            ))}
                            {run.instruments.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="py-8 text-center" style={{ color: 'var(--foreground-muted)' }}>
                                        No per-instrument attribution recorded for this run.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
